/**
 * Client side of Quote -> Proposal -> KYC -> Payment -> Document (board #245).
 *
 * WHY THIS FILE EXISTS AT ALL. The three pages in this flow already had a
 * client: `services/integrations.ts` + `hooks/useIntegrations.ts`. It did not
 * work, and could not have:
 *
 *  * `createProposal` POSTed `{quote_request_id, selected_insurer, proposer,
 *    nominee, kyc, addons_selected}` to `/integrations/policies/issue/`, whose
 *    `PolicyIssueSerializer` declares `quote_id, insurer_code, insurer_id,
 *    broker_id, environment, payment_id, payment_amount` and none of the above.
 *    A guaranteed 400 on every submission. It then read `response.policy_number`
 *    off the answer, a key that endpoint has never returned on that path.
 *  * `initiatePayment` sent `{application_id, payment_method}`;
 *    `PaymentInitiateView` requires `quote_id` AND `success_url` and 400s
 *    without either.
 *
 * Those two functions and the `ProposalPayload` / `PaymentInitiation` types live
 * under `services/` and `types/`, outside this change's file domain, so they are
 * left exactly as they were and this module is used instead -- the same reason
 * and the same shape as `pages/quotes/quoteEnquiries.ts`. It uses the shared
 * axios instance, so auth, refresh and throttling behave identically.
 *
 * THE ORDER OF THE STEPS IS THE BACKEND'S, NOT THE OLD UI'S. `PolicyIssueView`
 * requires `payment_id` and `payment_amount`, so issuance is the LAST call, not
 * the first: the proposal is recorded on OUR side, payment is taken, and only
 * then is the insurer asked to issue. The old flow called the issue endpoint
 * first and navigated to a policy number that did not exist yet.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';

import { expiryDayFromTimestamp, expiryToTimestamp } from './wizard/premiumOptions';

// ---------------------------------------------------------------------------
// 1. The proposal -- recorded on OUR row, whether or not an insurer API exists.
// ---------------------------------------------------------------------------

/** Exactly the fields `ProposalDetailsSerializer` declares. Anything else in
 *  the body is ignored by the server, so sending it would only be a lie to the
 *  next reader of this file. */
export interface ProposalDetailsPayload {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  date_of_birth: string;
  gender: string;
  pan_number: string;
  flat_number: string;
  street: string;
  city: string;
  state: string;
  pincode: string;
  nominee_first_name: string;
  nominee_last_name: string;
  nominee_date_of_birth: string;
  nominee_relation: string;
}

export interface StoredProposal {
  quote_request_id: string;
  address: string;
  proposal: Partial<ProposalDetailsPayload> & {
    address?: Record<string, string>;
    nominee?: Record<string, string>;
    updated_at?: string;
  };
}

export async function saveProposal(
  requestId: string,
  payload: ProposalDetailsPayload
): Promise<StoredProposal> {
  const response = await api.post<StoredProposal>(`/quotes/requests/${requestId}/proposal/`, payload);
  return response.data;
}

export function useStoredProposal(requestId: string | undefined) {
  return useQuery({
    queryKey: ['quote-proposal', requestId],
    queryFn: async () => {
      const response = await api.get<StoredProposal>(`/quotes/requests/${requestId}/proposal/`);
      return response.data;
    },
    enabled: !!requestId,
  });
}

export function useSaveProposal(requestId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProposalDetailsPayload) => saveProposal(requestId as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-proposal', requestId] });
      queryClient.invalidateQueries({ queryKey: ['quote', requestId] });
    },
  });
}

// ---------------------------------------------------------------------------
// 2. The options on a request, and which of them can be driven onward.
// ---------------------------------------------------------------------------

/** `pricing_source` is DERIVED by `QuoteListSerializer` from whether the insurer
 *  gave us its own reference for this price. It is the merge of #245 part 3 made
 *  legible: both kinds are `Quote` rows on one request, but only an API-priced
 *  one has an application the insurer can be asked to issue. */
export type PricingSource = 'insurer_api' | 'manual';

export interface QuoteOption {
  id: string;
  quote_number: string;
  insurer: string;
  insurer_code: string;
  insurer_name: string;
  insurer_quote_id: string;
  pricing_source: PricingSource;
  /** #399: the insurer's underwriter approved this figure on the Quote Desk. */
  insurer_approved?: boolean;
  /** Which cover this option priced (`comprehensive | tp_only | saod`) and the
   *  server's label for it. The public journey prices one insurer up to three
   *  ways on one request; without this staff see three identical GoDigit rows.
   *  Null for a premium typed in by hand. */
  cover_type?: string | null;
  cover_label?: string | null;
  /** The Product FK, not just its name -- the detail screen prefills an
   *  insurer/product pair from an existing option when it copies one. */
  product?: string;
  product_name?: string;
  total_premium: string;
  premium_total: number;
  status: string;
  /** Ordering/highlight only -- no insurer, payment or issuance path reads it,
   *  which is why it is the one field `optionEditPolicy` leaves editable on an
   *  API-priced row. It IS read on screen: `QuoteComparisonPage` floats it above
   *  the premium sort and badges it, and the options card badges it in place. */
  is_recommended?: boolean;
  valid_until: string;
  /** Files attached to THIS option. `QuoteOptionCreateSerializer` links an
   *  upload to both the option and the request, so the same row also appears in
   *  the request-level attachments list; here it is what says WHICH insurer's
   *  quote a PDF is. */
  documents?: { id: string; name: string; document_type_display?: string }[];
  /** #587: a life insurer's illustration (figures as strings), null on every other option. */
  illustration?: LifeIllustration | null;
}

export interface LifeIllustrationRow {
  year: number;
  age?: number;
  [figure: string]: string | number | undefined;
}

export interface LifeIllustration {
  reference_no?: string;
  uin?: string;
  policy_term?: number;
  pay_term?: number;
  sum_assured?: string;
  modal_premium_first_year?: string;
  modal_premium_later?: string;
  rows: LifeIllustrationRow[];
}

export function useQuoteOptions(requestId: string | undefined) {
  return useQuery({
    queryKey: ['quote-options', requestId],
    queryFn: async () => {
      const response = await api.get<QuoteOption[]>(`/quotes/requests/${requestId}/options/`);
      return response.data;
    },
    enabled: !!requestId,
  });
}

// ---------------------------------------------------------------------------
// 1b. Asking an insurer for a premium on a request that already exists.
// BOARD #278.
// ---------------------------------------------------------------------------

/**
 * Which insurers on this quote's broker could actually be asked.
 *
 * CONFIGURATION ONLY -- the endpoint calls nobody, so this may be asked on
 * every render. It reports what is switched on, never that a live call would
 * succeed; only the call itself can say that.
 */
export interface IntegratedInsurer {
  insurer_id: string;
  code: string;
  name: string;
  environment: string;
}

export interface IntegratedInsurers {
  quote_request_id: string;
  product_line: string;
  environment: string;
  product_line_has_adapter: boolean;
  insurers: IntegratedInsurer[];
}

export function useIntegratedInsurers(requestId: string | undefined) {
  return useQuery({
    queryKey: ['quote-integrated-insurers', requestId],
    queryFn: async () => {
      const response = await api.get<IntegratedInsurers>(
        `/quotes/requests/${requestId}/integrated-insurers/`
      );
      return response.data;
    },
    enabled: !!requestId,
  });
}

export interface FetchPremiumResult {
  insurer_code: string;
  insurer_name: string;
  environment: string;
  quotes: QuoteOption[];
  failures: { insurer_code?: string; error_code?: string; message?: string }[];
}

/**
 * Ask ONE integrated insurer for a live premium on this request.
 *
 * `quotes` carries only the rows this call created -- the server identifies them
 * by difference, because `request_quotes` reports a count of EVERY option on the
 * request and reporting that would claim a success on a request that already had
 * options and got nothing new.
 *
 * A failure comes back as a 502 whose `detail` is the INSURER'S own words
 * ("No GoDigit vehicle code for Maruti Swift VXI"), which names the row an admin
 * has to import. Show it verbatim.
 */
export function useFetchPremium(requestId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (insurerId: string) => {
      const response = await api.post<FetchPremiumResult>(
        `/quotes/requests/${requestId}/fetch-premium/`,
        { insurer: insurerId }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-options', requestId] });
      queryClient.invalidateQueries({ queryKey: ['quote', requestId] });
    },
  });
}

/**
 * B11 -- one catalogued vehicle a missing insurer vehicle code could mean, as
 * the fetch-premium 502 carries it (`apps.integrations.masters.
 * vehicle_code_candidates`, narrowed to the quote's own vehicle category).
 */
export interface VehicleCodeCandidate {
  variant_id: string;
  label: string;
  /** The insurer's own code for this vehicle -- shown so two trims with the
   *  same name can still be told apart. */
  insurer_code: string;
  make?: string;
  model?: string;
  variant?: string;
  fuel: string;
  cc: number | null;
}

export interface VehicleCodeMissing {
  detail: string;
  candidates: VehicleCodeCandidate[];
  vehicleCategory: string;
  /** Non-empty when the server will refuse to change this quote's vehicle
   *  (shared with the customer, or options already priced for the old one):
   *  the picker explains this instead of offering a choice. */
  changeBlocked: string;
}

/**
 * The picker's input when a fetch failed ONLY because the quote's vehicle has
 * no code for that insurer; `null` for every other failure, which keeps
 * showing the insurer's sentence as before. Defensive about shape: a
 * malformed candidate is dropped rather than rendered as a blank choice.
 */
export function vehicleCodeMissingFrom(err: unknown): VehicleCodeMissing | null {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const body = data as Record<string, unknown>;
  if (body.code !== 'vehicle_code_missing') return null;
  const candidates = Array.isArray(body.candidates)
    ? (body.candidates as VehicleCodeCandidate[]).filter(
        (c) => !!c && typeof c.variant_id === 'string' && c.variant_id !== ''
      )
    : [];
  return {
    detail: typeof body.detail === 'string' ? body.detail : '',
    candidates,
    vehicleCategory: typeof body.vehicle_category === 'string' ? body.vehicle_category : '',
    changeBlocked:
      typeof body.vehicle_change_blocked === 'string' ? body.vehicle_change_blocked : '',
  };
}

/**
 * B11 -- point this quote at the catalogued variant the broker chose from the
 * shortlist. The server accepts only a live variant of the same vehicle
 * category that carries a code for `insurerId`, on this broker's own quote,
 * and audits the change; it learns nothing for the next quote.
 */
export function useSetQuoteVehicleVariant(requestId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ variantId, insurerId }: { variantId: string; insurerId: string }) => {
      const response = await api.post<{
        quote_request_id: string;
        vehicle_variant: string;
        vehicle_variant_name: string;
      }>(`/quotes/requests/${requestId}/vehicle-variant/`, {
        variant: variantId,
        insurer: insurerId,
      });
      return response.data;
    },
    onSuccess: () => {
      // The detail screen shows the vehicle; it is stale until re-read.
      queryClient.invalidateQueries({ queryKey: ['quote', requestId] });
    },
  });
}

/** POST one hand-entered option onto an existing request -- the same endpoint
 *  and the same multipart body the wizard flushes its drafts through
 *  (`wizard/premiumOptions.postPremiumOptions`), used here for a single option
 *  added after the quote already exists. */
export function useAddQuoteOption(requestId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (form: FormData) => {
      const response = await api.post<QuoteOption>(`/quotes/requests/${requestId}/options/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-options', requestId] });
      // The request payload nests its own `quotes` array, so the detail screen
      // is stale too until this is re-read.
      queryClient.invalidateQueries({ queryKey: ['quote', requestId] });
    },
  });
}

/** Only the fields `optionEditPolicy` allows. Sent as a PARTIAL update, and
 *  deliberately only the keys that actually changed: `QuoteDetailSerializer`
 *  accepts every premium column, and echoing back the unchanged ones would make
 *  an accidental clobber indistinguishable from an intended edit in the log. */
export interface QuoteOptionPatch {
  total_premium?: string;
  valid_until?: string;
  is_recommended?: boolean;
}

export function useUpdateQuoteOption(requestId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ optionId, patch }: { optionId: string; patch: QuoteOptionPatch }) => {
      // `QuoteViewSet` is a plain ModelViewSet, so this is its own PATCH -- the
      // options endpoint on the REQUEST is create-only. `get_queryset` there is
      // broker-scoped, so another tenant's option 404s rather than updating.
      const response = await api.patch<QuoteOption>(`/quotes/quotes/${optionId}/`, patch);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-options', requestId] });
      queryClient.invalidateQueries({ queryKey: ['quote', requestId] });
    },
  });
}

/**
 * WHAT MAY BE CHANGED ON AN OPTION THAT ALREADY EXISTS.
 *
 * READ `backend/apps/quotes/serializers.py:258` BEFORE CHANGING THIS. The rule
 * is not about tidiness, it is about who else is holding the figure.
 *
 *  * A `manual` option was typed in by this broker and nothing outside this
 *    database has ever seen it. Its onward path is already the manual one --
 *    record the payment, upload the issued policy copy -- so rewriting the
 *    amount rewrites the only copy there is. Fully editable.
 *
 *  * An `insurer_api` option exists because the insurer priced it and handed
 *    back `insurer_quote_id`, and the insurer is STILL HOLDING an application
 *    at that price. `PaymentInitiateView` initiates against that reference and
 *    `PolicyIssueView` hands `quote_id` to the adapter; neither reads our
 *    `total_premium`. Editing it here would show the customer one number, take
 *    another at the gateway, and issue at a third -- an action that reports
 *    success with nothing behind it, which is this platform's signature bug.
 *    So the figures are LOCKED and the broker is offered a copy instead: a new
 *    `manual` row on the same request, carrying no `insurer_quote_id`, sitting
 *    beside the insurer's untouched one. Both survive, and `pricing_source`
 *    keeps telling the truth about each.
 *
 * `is_recommended` is editable either way, because no insurer, payment or
 * issuance path reads it -- rewriting it cannot make our figure disagree with
 * the insurer's.
 *
 * WHAT IT ACTUALLY DOES, exactly: `QuoteComparisonPage` sorts it above the
 * premium order and badges it "Recommended", and `QuotePremiumOptionsCard`
 * badges the row. That is the whole of it. It does NOT mark anything on the
 * customer's own quote link: `pages/public/PublicQuote.tsx` renders
 * `quote.quotes` in server order and has no reader for this field. The server
 * already sends it there (`PublicQuoteRequestSerializer` nests
 * `QuoteListSerializer`), so that page is a rendering change away -- it is
 * outside this file's domain and is written up in the report. Until it lands,
 * do not let any label here promise the customer sees this.
 *
 * THIS IS PRESENTATION, NOT A PERMISSION. `PATCH /quotes/quotes/{id}/` will
 * still rewrite an API-priced premium for anything that asks. The server-side
 * refusal that would make this a rule is not in this change's file domain.
 */
export interface OptionEditPolicy {
  /** May the premium and expiry be rewritten on the row itself? */
  canEditFigures: boolean;
  /** Shown verbatim when they may not. Says who else holds the figure. */
  lockedReason: string;
}

export function optionEditPolicy(source: PricingSource): OptionEditPolicy {
  if (source === 'manual') {
    return { canEditFigures: true, lockedReason: '' };
  }
  return {
    canEditFigures: false,
    lockedReason:
      'This price came from the insurer\u2019s API, and the insurer holds an application at this price. ' +
      'Changing the figure here would not change theirs \u2014 payment and issuance would still run at the ' +
      'price they quoted. Copy it as a manual option instead: the adjusted figure goes on a new option ' +
      'alongside this one, and this one stays exactly as the insurer priced it.',
  };
}

/** What the edit form on the quote screen holds while one option is open. */
export interface OptionEditForm {
  total_premium: string;
  valid_until: string;
  is_recommended: boolean;
}

/**
 * The PATCH body for one edit: the diff between the option as stored and the
 * form as typed, NARROWED BY `optionEditPolicy` first.
 *
 * The narrowing is the point, and it is deliberately not left to "the Amount
 * input was not rendered". The form is seeded from the option, so on screen the
 * two cannot diverge on an API-priced row -- but the options list refetches
 * (after any add or save), and a refetch under an open editor moves the stored
 * figures beneath a form still holding the old ones. A diff alone would then
 * PATCH the insurer's premium back to a stale value nobody typed.
 *
 * Only what CHANGED is sent, for a second reason: `QuoteDetailSerializer`
 * accepts every premium column, so echoing the untouched ones back would make
 * an accidental clobber indistinguishable from a deliberate edit in the log.
 */
export function buildOptionPatch(
  option: Pick<QuoteOption, 'pricing_source' | 'total_premium' | 'valid_until' | 'is_recommended'>,
  form: OptionEditForm
): QuoteOptionPatch {
  const patch: QuoteOptionPatch = {};

  if (optionEditPolicy(option.pricing_source).canEditFigures) {
    if (form.total_premium.trim() !== String(option.total_premium ?? '').trim()) {
      patch.total_premium = form.total_premium.trim();
    }
    if (form.valid_until && form.valid_until !== expiryDayFromTimestamp(option.valid_until)) {
      patch.valid_until = expiryToTimestamp(form.valid_until);
    }
  }

  // Presentation only, so it is editable on both kinds -- see optionEditPolicy.
  if (form.is_recommended !== Boolean(option.is_recommended)) {
    patch.is_recommended = form.is_recommended;
  }

  return patch;
}

/**
 * Can THIS option be taken through proposal -> payment -> issuance, and if not
 * why not -- answered from stored configuration, without spending a credential
 * or making an insurer call.
 *
 * `blockers` are whole sentences written by the server, several of them
 * word-for-word what the endpoint further down the flow would have answered
 * (`No integration configured for X in Y`, `X does not support payment
 * initiation`). RENDER THEM VERBATIM. Re-wording them here is how the screen
 * starts saying something the API does not.
 */
export interface IssuanceReadiness {
  quote_id: string;
  quote_number: string;
  insurer_id: string | null;
  insurer_code: string;
  insurer_name: string;
  insurer_quote_id: string;
  pricing_source: PricingSource;
  total_premium: string;
  environment: string | null;
  api_version: string | null;
  integration_configured: boolean;
  adapter_available: boolean;
  supports_payment_initiation: boolean;
  can_issue_via_api: boolean;
  blockers: string[];
  relay_configured: boolean;
  /** BOARD #258. Whether the insurer already holds an application for this
   *  option, and which id it is. `proposal_recorded` is the boolean the
   *  proposal button binds to: false means POST the proposal route, true means
   *  PUT it (or go straight to payment). Optional so a cached response from
   *  before #258 degrades to "we do not know" rather than to a confident wrong
   *  answer -- `needsProposal` below reads them defensively. */
  proposal_recorded?: boolean;
  insurer_application_id?: string;
  insurer_policy_number?: string;
  /** 'one_step' | 'two_step' | 'conditional' -- the adapter's own declaration.
   *  Only the last two have a proposal step at all. */
  issuance_flow?: string;
  needs_proposal?: boolean;
}

/**
 * Does this option still need a proposal opened with the insurer?
 *
 * Read off the server's own `needs_proposal` when it is there. An older server
 * (or a cached readiness from before board #258) does not send it, and the
 * honest answer then is FALSE -- "we cannot tell, so do not offer a button
 * whose first act is an insurer call". Guessing true would put a call the
 * broker did not ask for behind a button that might do nothing.
 */
export function needsProposal(readiness: IssuanceReadiness | undefined): boolean {
  if (!readiness) return false;
  if (typeof readiness.needs_proposal === 'boolean') return readiness.needs_proposal;
  return false;
}

// ---------------------------------------------------------------------------
// 2b. The insurer's proposal. BOARD #258.
// ---------------------------------------------------------------------------

/** What `PolicyIssuanceService.record_proposal` persisted on the quote. */
export interface InsurerProposal {
  application_id: string;
  policy_number: string;
  status: string;
  policy_state?: string;
  enquiry_id?: string;
  created_at?: string;
  recorded_at?: string;
}

export interface InsurerProposalResult {
  quote_id: string;
  status: string;
  operation?: string;
  insurer_code: string;
  proposal: InsurerProposal;
  environment?: string;
}

/**
 * POST opens the insurer's proposal; PUT amends the one they already hold.
 *
 * These are two different acts and they are deliberately two different calls.
 * "Fixing" a proposal by creating a second one leaves two applications on the
 * insurer's book and pays for one of them, which is why the server answers 409
 * rather than silently opening another.
 */
export async function createInsurerProposal(quoteId: string, force = false): Promise<InsurerProposalResult> {
  const response = await api.post<InsurerProposalResult>(
    `/integrations/quotes/${quoteId}/insurer-proposal/`,
    force ? { force: true } : {}
  );
  return response.data;
}

export async function updateInsurerProposal(quoteId: string): Promise<InsurerProposalResult> {
  const response = await api.put<InsurerProposalResult>(
    `/integrations/quotes/${quoteId}/insurer-proposal/`,
    {}
  );
  return response.data;
}

export function useInsurerProposal(quoteId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mode }: { mode: 'create' | 'update' }) =>
      mode === 'create'
        ? createInsurerProposal(quoteId as string)
        : updateInsurerProposal(quoteId as string),
    onSuccess: () => {
      // The readiness IS the screen's model of this; leaving it stale would
      // leave the button offering to open a proposal that now exists.
      queryClient.invalidateQueries({ queryKey: ['issuance-readiness', quoteId] });
    },
  });
}

export function useIssuanceReadiness(quoteId: string | undefined) {
  return useQuery({
    queryKey: ['issuance-readiness', quoteId],
    queryFn: async () => {
      const response = await api.get<IssuanceReadiness>(
        `/integrations/quotes/${quoteId}/issuance-readiness/`
      );
      return response.data;
    },
    enabled: !!quoteId,
  });
}

// ---------------------------------------------------------------------------
// 3. Payment, then issuance. In that order, because the backend says so.
// ---------------------------------------------------------------------------

/** `quote_id` and `success_url` are both REQUIRED by `PaymentInitiateView`;
 *  `application_id` is optional and falls back to the quote's own
 *  `insurer_quote_id`. */
export interface PaymentInitiatePayload {
  quote_id: string;
  success_url: string;
  cancel_url: string;
  environment?: string;
}

export interface PaymentInitiateResult {
  quote_id: string;
  insurer_code: string;
  /** Whatever the insurer's gateway returned. GoDigit's key is not fixed across
   *  adapters, so the page looks for the first of several rather than assuming
   *  one -- and shows the raw answer when it finds none, instead of pretending
   *  the payment was taken. */
  redirect_url?: string;
  paymentUrl?: string;
  payment_url?: string;
  [key: string]: unknown;
}

export async function initiatePayment(payload: PaymentInitiatePayload): Promise<PaymentInitiateResult> {
  const response = await api.post<PaymentInitiateResult>('/integrations/payment/initiate/', payload);
  return response.data;
}

/** The gateway URL out of an adapter's own answer, or null. Null means "the
 *  insurer did not give us anywhere to send the customer", which the page must
 *  say rather than navigating on as though it had. */
export function gatewayUrlFrom(result: PaymentInitiateResult | undefined): string | null {
  if (!result) return null;
  for (const key of ['redirect_url', 'payment_url', 'paymentUrl', 'paymentLink', 'url']) {
    const value = result[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

/** Exactly `PolicyIssueSerializer`. `broker_id` is deliberately absent: the view
 *  resolves the broker from the authenticated principal and only ACCEPTS a body
 *  value that matches, so sending one from a broker login can only ever produce
 *  a 403 it would not otherwise have had. */
export interface IssuePolicyPayload {
  quote_id: string;
  insurer_code: string;
  insurer_id: string;
  environment: string;
  payment_id: string;
  payment_amount: string;
  payment_mode?: string;
  transaction_reference?: string;
}

/**
 * `policy_number` / `policy_id` are NULLABLE ON PURPOSE.
 *
 * `PolicyIssueView` derives them by re-reading the row `PolicyIssuanceService`
 * says it persisted; when it cannot, it answers 200 with them null and a message
 * saying the insurer issued but this platform has no local record. That is a
 * real outcome and the screen has to show it. Treating null as success is the
 * exact bug this flow was built out of.
 */
export interface IssuePolicyResult {
  status: string;
  message?: string;
  policy_number: string | null;
  policy_id: string | null;
  insurer_policy_number?: string;
  policy_document_url?: string | null;
  [key: string]: unknown;
}

export async function issuePolicy(payload: IssuePolicyPayload): Promise<IssuePolicyResult> {
  const response = await api.post<IssuePolicyResult>('/integrations/policies/issue/', payload);
  return response.data;
}

/** The broker records money taken outside the platform. KYC-gated on the server
 *  (`record_payment` refuses anything that is not `kyc_approved`) and refused
 *  for a quote that names no customer -- both are shown to the user as the
 *  server words them. */
export interface RecordPaymentPayload {
  payment_reference: string;
  payment_date: string;
  payment_mode: string;
}

export async function recordPayment(requestId: string, payload: RecordPaymentPayload): Promise<unknown> {
  const response = await api.post(`/quotes/requests/${requestId}/record-payment/`, payload);
  return response.data;
}

// ---------------------------------------------------------------------------
// 4. Reading the two merge-relevant fields off a compare-screen row.
// ---------------------------------------------------------------------------

/**
 * `QuoteListSerializer` sends `id`, `insurer_quote_id` and `pricing_source` on
 * every row, but the shared `InsurerQuote` type in `types/integrations.ts`
 * declares only the second of the three -- that file is outside this change's
 * file domain, so the rows are narrowed here instead of being widened there.
 *
 * Defensive rather than a cast: an older server, or a cached response from
 * before `pricing_source` existed, must degrade to "we cannot tell" and not to
 * a confident wrong answer. Absent `pricing_source`, it is re-derived from
 * `insurer_quote_id` by the same rule the server uses.
 */
export function optionExtras(quote: unknown): {
  id: string | null;
  insurerQuoteId: string;
  pricingSource: PricingSource;
  /** The broker's own pick. Absent (an older server, a cached row) reads as
   *  false -- "nothing was recommended", never "everything was". */
  isRecommended: boolean;
} {
  const row = (quote ?? {}) as Record<string, unknown>;
  const id = typeof row.id === 'string' && row.id ? row.id : null;
  const insurerQuoteId = typeof row.insurer_quote_id === 'string' ? row.insurer_quote_id.trim() : '';
  const isRecommended = row.is_recommended === true;
  const declared = row.pricing_source;
  const pricingSource: PricingSource =
    declared === 'insurer_api' || declared === 'manual'
      ? declared
      : insurerQuoteId
      ? 'insurer_api'
      : 'manual';
  return { id, insurerQuoteId, pricingSource, isRecommended };
}
